import { SeasonMetric, SeasonTab, SeasonUrlState } from '@/types';
import { UrlCodec } from '@/utils/urlState';

const tabs: readonly SeasonTab[] = ['overview', 'standings', 'team', 'players', 'monthly'];
const metrics: readonly SeasonMetric[] = ['points', 'goals'];

const positiveId = (value: string | null): number | undefined => {
  if (!value || !/^[1-9]\d*$/.test(value)) return undefined;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : undefined;
};

const dateOnly = (value: string | null): string | undefined => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value ? undefined : value;
};

export const parseSeasonUrl = (search: URLSearchParams): SeasonUrlState => {
  const rawTab = search.get('tab') as SeasonTab | null;
  const rawMetric = search.get('metric') as SeasonMetric | null;
  const tab = rawTab && tabs.includes(rawTab) ? rawTab : 'overview';
  let from = dateOnly(search.get('from'));
  let to = dateOnly(search.get('to'));
  if (from && to && from > to) {
    from = undefined;
    to = undefined;
  }
  return {
    season: positiveId(search.get('season')),
    tab,
    from,
    to,
    metric: rawMetric && metrics.includes(rawMetric) ? rawMetric : 'points',
    player: tab === 'players' ? positiveId(search.get('player')) : undefined,
  };
};

export const serializeSeasonUrl = (state: SeasonUrlState): URLSearchParams => {
  const params = new URLSearchParams();
  if (state.season && Number.isSafeInteger(state.season) && state.season > 0) params.set('season', String(state.season));
  if (state.from && dateOnly(state.from)) params.set('from', state.from);
  if (state.to && dateOnly(state.to) && (!state.from || state.from <= state.to)) params.set('to', state.to);
  if (state.tab !== 'overview' && tabs.includes(state.tab)) params.set('tab', state.tab);
  if (state.metric !== 'points' && metrics.includes(state.metric)) params.set('metric', state.metric);
  if (state.tab === 'players' && state.player && Number.isSafeInteger(state.player) && state.player > 0) params.set('player', String(state.player));
  return params;
};

export const seasonUrlCodec: UrlCodec<SeasonUrlState> = {
  parse: parseSeasonUrl,
  serialize: serializeSeasonUrl,
};
